import seededRandom, { getSampleOrgTotal } from "./seededRandom";

/**
 * 참가자 관리(샘플) 가상 데이터 + 상태 정의
 * - 실제 API 연동 전까지 GET /users, program-detail 응답과 비슷한 형태로 생성합니다.
 * - 같은 조회 범위·참가자·날짜면 항상 같은 값이 나오도록 seededRandom을 사용합니다.
 */

// 참가자 현재 상태 — 목록 우측 배지와 상세 상단 "현재 상태" 태그에 함께 사용
// 디스트레스 단계 기준: 안정 ≤0.16 / 주의 0.17~0.60(주의·경고) / 고위험 ≥0.61 (개인 상세 DISTRESS_LEVELS와 같은 경계)
export const RISK_STATUS = {
  crisis: {
    filter: "crisis_episode",
    badge: "🚨 위기",
    tag: "🚨 긴급 위기 · 즉시 확인 필요",
    badgeSx: { backgroundColor: "#B91C1C", color: "#FFFFFF" },
    tagSx: { backgroundColor: "#B91C1C", color: "#FFFFFF" },
  },
  episode: {
    filter: "crisis_episode",
    badge: "⚡ 에피소드",
    tag: "⚡ 급성 악화(에피소드) 관리 필요",
    badgeSx: { backgroundColor: "#FFF1F2", color: "#DC2626", border: "1.5px solid #EF4444" },
    tagSx: { backgroundColor: "#FFF1F2", color: "#DC2626", border: "1.5px solid #EF4444" },
  },
  high: {
    filter: "high",
    dot: "#EF4444",
    label: "고위험",
    tag: "🔴 고위험 상태 지속 · 집중 관찰 필요",
    tagSx: { backgroundColor: "#FEE2E2", color: "#B91C1C" },
  },
  caution: {
    filter: "caution",
    dot: "#FACC15",
    label: "주의",
    tag: "🟡 주의 단계 · 변화 관찰 필요",
    tagSx: { backgroundColor: "#FEF9C3", color: "#854D0E" },
  },
  stable: {
    filter: "stable",
    dot: "#22C55E",
    label: "안정",
    tag: "🟢 안정 유지 중",
    tagSx: { backgroundColor: "#DCFCE7", color: "#15803D" },
  },
};

// 좌측 [위험도] 드롭다운 옵션
export const RISK_FILTER_OPTIONS = [
  { value: "전체", label: "위험도 전체" },
  { value: "crisis_episode", label: "🚨 위기·에피소드" },
  { value: "high", label: "🔴 고위험" },
  { value: "caution", label: "🟡 주의" },
  { value: "stable", label: "🟢 안정" },
];

// 종합현황 도넛 4단계 → 좌측 위험도 필터 (🟠 중등도/경고는 "주의" 옵션에 포함)
export const DISTRESS_TO_RISK_FILTER = { stable: "stable", caution: "caution", warning: "caution", high: "high" };

// 종합현황 우선 확인 대상자 표의 위험 등급 → 참가자 현재 상태
const TRIAGE_LEVEL_TO_STATUS = { crisis: "crisis", episode: "episode", chronic: "high", dropout: "caution" };

const STATUS_WEIGHTS = [
  ["stable", 0.42],
  ["caution", 0.33],
  ["high", 0.12],
  ["episode", 0.09],
  ["crisis", 0.04],
];
const BASE_DISTRESS = { stable: 0.09, caution: 0.33, high: 0.68, episode: 0.42, crisis: 0.72 };
// 상태별 PHQ-9 점수 범위 (GAD-7은 이 범위의 약 80%)
const PHQ_RANGE = { stable: [0, 6], caution: [4, 12], high: [10, 20], episode: [8, 18], crisis: [15, 25] };

const SURNAMES = ["김", "이", "박", "최", "정", "강", "조", "윤", "장", "임", "한", "오", "서", "신", "권", "고"];
const GIVEN_NAMES = [
  "민준", "서연", "도윤", "지우", "하윤", "시우", "서아", "준서", "예린", "현우",
  "지호", "수아", "건우", "채원", "유진", "태민", "다은", "승현", "소율", "재원", "경덕", "선재",
];

// ─── 날짜 유틸 ─────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
export const toDateString = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const parseDate = (str) => {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};
export const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};
const getDateList = (start, end) => {
  const dates = [];
  for (let d = parseDate(start); d <= parseDate(end); d = addDays(d, 1)) dates.push(toDateString(d));
  return dates;
};
const daysAgo = (dateStr) => Math.round((parseDate(toDateString(new Date())) - parseDate(dateStr)) / 86400000);

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const round2 = (value) => Math.round(value * 100) / 100;

const pickStatus = (r) => {
  let acc = 0;
  for (const [status, weight] of STATUS_WEIGHTS) {
    acc += weight;
    if (r < acc) return status;
  }
  return "stable";
};

/** 검사 회차 이력 (최근 회차가 배열의 첫 번째) — latestScore가 주어지면 최근 회차 점수로 고정 */
const buildHistory = (type, count, status, rand, latestScore) => {
  const [min, max] = PHQ_RANGE[status];
  const scale = type === "gad" ? 0.8 : 1;
  const cap = type === "gad" ? 21 : 27;
  const today = new Date();
  return Array.from({ length: count }, (_, i) => {
    const score =
      i === 0 && typeof latestScore === "number"
        ? latestScore
        : Math.min(cap, Math.round((min + rand() * (max - min)) * scale));
    return {
      attempt: count - i,
      completed_at: toDateString(addDays(today, -(i * 30 + 3 + Math.floor(rand() * 10)))),
      total_score: score,
    };
  });
};

/**
 * 참가자 1명 생성
 * @param {string} seed - 참가자 고유 시드 (user_id로도 사용)
 * @param {object} overrides - 이름/나이/성별/상태/활동/최근 검사 점수 고정값 (종합현황에서 넘어온 대상자 재현용)
 */
export const createParticipant = (seed, overrides = {}) => {
  const rand = seededRandom(`participant|${seed}`);
  const status = overrides.status ?? pickStatus(rand());
  const name =
    overrides.name ??
    `${SURNAMES[Math.floor(rand() * SURNAMES.length)]}${GIVEN_NAMES[Math.floor(rand() * GIVEN_NAMES.length)]}`;
  const age = overrides.age ?? 20 + Math.floor(rand() * 45);
  const gender = overrides.gender ?? (rand() < 0.5 ? "M" : "F");
  const activity = overrides.activity ?? (rand() < 0.22 ? "inactive" : "active");

  // 최근 검사 점수가 지정된 경우: null이면 미실시(0회), 숫자면 최소 1회 이상
  const historyCount = (latest, defaultCount) => {
    if (latest === null) return 0;
    if (typeof latest === "number") return Math.max(1, defaultCount);
    return defaultCount;
  };
  const phqCount = historyCount(overrides.latestPhq, Math.floor(rand() * 4));
  const gadCount = historyCount(overrides.latestGad, Math.floor(rand() * 3));

  return {
    user_id: seed,
    name,
    age,
    gender,
    activity,
    status,
    baseDistress: BASE_DISTRESS[status],
    phq_history: buildHistory("phq", phqCount, status, rand, overrides.latestPhq),
    gad_history: buildHistory("gad", gadCount, status, rand, overrides.latestGad),
  };
};

/** 조회 범위의 전체 참가자 목록 (전체 인원 수는 종합현황 KPI의 총 인원과 같음) */
export const buildParticipants = (scopeKey) =>
  Array.from({ length: getSampleOrgTotal(scopeKey) }, (_, i) => createParticipant(`${scopeKey}-${i + 1}`));

/** 종합현황 [상세 보기]로 넘어온 대상자 행을 참가자 데이터로 변환 */
export const participantFromTriageRow = (scopeKey, row) =>
  createParticipant(`${scopeKey}-triage-${row.id}`, {
    name: row.name,
    age: row.age,
    gender: row.gender,
    activity: row.activity,
    status: TRIAGE_LEVEL_TO_STATUS[row.level],
    latestPhq: row.phq,
    latestGad: row.gad,
  });

/**
 * 날짜별 기관 평균 지표 — 참가자 상세의 "기관 평균 추이"와 기관 통계 리포트 섹션 1이 같은 값을 쓰도록 공용화
 * - 며칠 단위로 천천히 오르내리는 흐름(파형) + 일별 잡음
 */
export const getOrgDailyAverages = (scopeKey, date) => {
  const orgRand = seededRandom(`${scopeKey}|${date}|org`);
  const dayNumber = Math.round(parseDate(date).getTime() / 86400000);
  const phase = seededRandom(`${scopeKey}|phase`)() * Math.PI * 2;
  const wave = Math.sin(dayNumber / 6 + phase) * 0.06;
  return {
    org_symptom_avg: round2(0.38 + wave + orgRand() * 0.1),
    org_emo_avg: round2(0.22 + wave + orgRand() * 0.1),
    org_distress_avg: round2(0.26 + wave + orgRand() * 0.1),
  };
};

/** 조회 기간의 날짜별 기관 평균 디스트레스 [{ date, org_distress_avg }] */
export const buildOrgDistressSeries = (scopeKey, start, end) =>
  getDateList(start, end).map((date) => ({ date, org_distress_avg: getOrgDailyAverages(scopeKey, date).org_distress_avg }));

/**
 * 기간 내 날짜별 지표 (program-detail의 daily_scores 형태)
 * - 잠잠(미활동) 참가자는 최근 7일 기록이 없음
 * - 에피소드/위기 상태는 최근 며칠에 급상승 + 에피소드 발생
 */
export const buildDailyScores = (participant, scopeKey, start, end) =>
  getDateList(start, end).map((date) => {
    const rand = seededRandom(`${participant.user_id}|${date}`);
    const org = getOrgDailyAverages(scopeKey, date);

    // 개인 기준선 — 급상승(spike)을 뺀 평소 수준이 천천히 흔들리는 값 (기록이 없는 날도 이어지도록 항상 계산)
    const baseline = round2(
      clamp01(participant.baseDistress + (seededRandom(`${participant.user_id}|baseline|${date}`)() - 0.5) * 0.04),
    );
    const ago = daysAgo(date);
    const isAcute = ["episode", "crisis"].includes(participant.status);
    // 에피소드·위기 상태(활발)는 "진행 중"과 맞도록 최근 2일 기록을 항상 남김
    const keepRecent = isAcute && participant.activity === "active" && ago >= 0 && ago <= 1;
    const noRecord = !keepRecent && ((participant.activity === "inactive" && ago < 7) || rand() < 0.12);
    if (noRecord) {
      return {
        date,
        symptom_score: null,
        emo_score: null,
        distress_score: null,
        baseline_score: baseline,
        records: null,
        is_episode: false,
        ...org,
      };
    }

    const recentSpike = isAcute && ago >= 0 && ago <= 3 ? 0.18 : 0;
    const distress = clamp01(participant.baseDistress + recentSpike + (rand() - 0.5) * 0.16);
    const isEpisode = keepRecent || (recentSpike > 0 && rand() < 0.6) || (participant.status === "caution" && rand() < 0.05);
    // 반영된 기록 — 점수가 있는 날은 감정체크·챗봇 대화 중 최소 하나는 완료
    const emotionCheck = rand() < 0.85;
    const chatbot = !emotionCheck || rand() < 0.55;

    return {
      date,
      symptom_score: round2(clamp01(distress * 0.85 + 0.12 + (rand() - 0.5) * 0.1)),
      // 감정 점수 = 부정 정서 — 디스트레스가 높을수록 함께 높아짐
      emo_score: round2(clamp01(distress * 0.75 + (rand() - 0.5) * 0.12)),
      distress_score: round2(distress),
      baseline_score: baseline,
      records: { emotion_check: emotionCheck, chatbot },
      is_episode: isEpisode,
      ...org,
    };
  });

/** 기간 요약 (program-detail의 week 형태: symptom_avg, org_symptom_avg, episode_count, org_respondents 등) */
export const buildWeekSummary = (dailyScores, scopeKey, start, end) => {
  const mean = (key) => {
    const values = dailyScores.map((d) => d[key]).filter((v) => typeof v === "number");
    return values.length ? round2(values.reduce((a, b) => a + b, 0) / values.length) : null;
  };
  const rand = seededRandom(`${scopeKey}|${start}|${end}|week`);
  const orgTotal = getSampleOrgTotal(scopeKey);

  return {
    period_start: start,
    period_end: end,
    record_days: dailyScores.filter((d) => typeof d.distress_score === "number").length,
    symptom_avg: mean("symptom_score"),
    emo_avg: mean("emo_score"),
    distress_avg: mean("distress_score"),
    org_symptom_avg: mean("org_symptom_avg"),
    org_emo_avg: mean("org_emo_avg"),
    org_distress_avg: mean("org_distress_avg"),
    episode_count: dailyScores.filter((d) => d.is_episode).length,
    // 조회 종료일 기준 최근 2일 안에 에피소드가 있으면 "진행 중"으로 봄
    recent_episode: dailyScores.slice(-2).some((d) => d.is_episode),
    org_episode_avg: Math.round((0.2 + rand() * 1.0) * 10) / 10,
    org_respondents: Math.round(orgTotal * (0.55 + rand() * 0.3)),
    org_total: orgTotal,
  };
};
